import { HomeOutlined } from '@ant-design/icons';
import { Layout } from 'antd';
import React from 'react';
import { useMatches } from 'react-router-dom';
import { Breadcrumb, BreadcrumbItem } from './Breadcrumb';

type PageBreadcrumb = React.ReactNode | false;

export interface PageProps {
  /** When true, removes paddings from the content area. */
  isFullScreen?: boolean;
  /** The extra content to be rendered in the top right corner of the page. */
  extras?: React.ReactNode;
  children?: React.ReactNode;
  /**
   * This is for overriding the breadcrumb section. Do not use this to define your own breadcrumbs.
   * Instead, use the `crumb` property of the route item.
   *
   * - When leave untouched or passed `undefined`, the breadcrumb section will be rendered based on the route item config.
   * - When `false`, the breadcrumb section will be disabled. There will be nothing rendered on the top left corner.
   * - When a ReactNode passed, the node will be rendered instead of the breadcrumbs.
   */
  breadcrumb?: PageBreadcrumb;
}

/**
 * Page component.
 * Wrapping your pages with this component will give you the following features:
 * - Breadcrumbs
 * - Full screen mode
 * - Extra content area on the top right corner
 * - Automatically setting the document title based on the breadcrumbs
 * - Override the breadcrumb section with your own content
 *
 * You can implement your pages without wrapping them with this. However, you will have to implement the above features yourself if needed.
 *
 * Example usage:
 *
 * ```jsx
 * <Page {...props}>
 *  <div>My page content</div>
 * </Page>
 * ```
 */
export default function Page(props: PageProps) {
  const matches: any = useMatches();
  const match = matches?.[matches?.length - 1];
  const isFullScreen = props.isFullScreen !== undefined ? props.isFullScreen : !!match?.handle?.fullScreen;
  const extras = props.extras !== undefined ? props.extras : match?.handle?.extras;

  return (
    <Layout.Content id="decaf-content" style={{ flex: 1, minHeight: '100%' }}>
      <DecafLayoutPageTitle />
      <DecafLayoutBreadcrumb breadcrumb={props.breadcrumb}>{extras}</DecafLayoutBreadcrumb>

      <div id="decaf-layout-content-outlet" className={isFullScreen ? 'full-screen' : ''}>
        {props.children}
      </div>
    </Layout.Content>
  );
}

function getAppNameFromDocumentTitle() {
  const title = document.title;
  if (title.split(' | ').length > 1) {
    return title.split(' | ')[title.split(' | ').length - 1];
  } else {
    return title;
  }
}

function getRenderedTitle() {
  // const div = document.createElement('div');
  // flushSync(() => {
  //   createRoot(div).render(title);
  // });
  // return div.textContent || '';
  const bc = document.getElementById('decaf-layout-breadcrumb');
  const lastLi = bc?.querySelector('li:last-child');
  return (lastLi?.textContent || '').trim();
}

function DecafLayoutPageTitle() {
  const matches: any = useMatches();
  const crumbs = matches
    .filter((match: any) => Boolean(match.handle?.crumb))
    .map((match: any) => match.handle.crumb(match.data));
  const appName = getAppNameFromDocumentTitle();
  const title = crumbs?.[crumbs.length - 1]?.title;
  const rTitle = typeof title === 'string' ? title : getRenderedTitle();

  if (rTitle) {
    document.title = `${rTitle} | ${appName}`;
  } else {
    document.title = appName;
  }

  return null;
}

function DecafLayoutBreadcrumb(props: { children?: React.ReactNode; breadcrumb?: PageBreadcrumb }) {
  const matches = useMatches();

  if (props.breadcrumb === false) {
    return (
      <div id="decaf-layout-breadcrumb" style={{ justifyContent: 'flex-end' }}>
        {props.children}
      </div>
    );
  } else if (props.breadcrumb) {
    return (
      <div id="decaf-layout-breadcrumb">
        {props.breadcrumb}
        {props.children}
      </div>
    );
  } else {
    const crumbs = matches
      .filter((match: any) => Boolean(match.handle?.crumb))
      .map((match: any) => match.handle.crumb(match.data));

    const breadcrumb: BreadcrumbItem[] = [{ href: '/', title: <HomeOutlined /> }, ...crumbs].map((x, i) => ({
      ...x,
      _key: i,
    }));

    if (breadcrumb.length < 2) {
      return <></>;
    }

    return (
      <div id="decaf-layout-breadcrumb">
        <Breadcrumb items={breadcrumb} />
        {props.children}
      </div>
    );
  }
}
